import React, { ReactNode, useMemo } from 'react'

import { MapProvider, useMap } from './MapContext'
import { useLands } from '../Lands/contexts/LandsContext'
import { useBuildings } from '../Buildings/contexts/BuildingsContext'
import { useRouter } from '@popety-io/ui'

interface ExploreProviderProps {
  children: ReactNode
}

/**
 * Composed provider that combines all Explore-related contexts
 * MapProvider provides UI state, while BuildingsContext/LandsContext provide data and filters
 */
export const ExploreProvider: React.FC<ExploreProviderProps> = ({
  children,
}) => {
  return <MapProvider>{children}</MapProvider>
}

// Re-export hooks for convenience
export { useMap }

// Main hook for accessing all Explore context data
export const useExploreContext = () => {
  const map = useMap()
  const { query, updateQuery } = useRouter()
  const currentMode = (query.mode as 'plot' | 'building') || 'plot'

  // Get contexts for both modes
  const landsContext = useLands()
  const buildingsContext = useBuildings()

  // Use the context based on current mode
  const activeContext = currentMode === 'plot' ? landsContext : buildingsContext

  return useMemo(
    () => ({
      ...map,
      // Add filter-related properties from active context
      filters: activeContext.filters,
      updateFilter: activeContext.updateFilter,
      updateMultipleFilters: activeContext.updateMultipleFilters,
      clearFilters: activeContext.clearFilters,
      filterMode: activeContext.filterMode,
      setFilterMode: activeContext.setFilterMode,
      mode: currentMode,
      setMode: (newMode: 'plot' | 'building') => updateQuery({ mode: newMode }),
      // Data from active context
      data: activeContext.lands || activeContext.buildings || [],
      loading: activeContext.loading,
      error: activeContext.error,
      total: activeContext.total,
      // Manual search trigger
      triggerSearch: activeContext.triggerSearch,
    }),
    [map, activeContext, currentMode, updateQuery]
  )
}

// Legacy hook for backward compatibility
export const useExploreMap = useExploreContext
